import {useEffect, useMemo, useRef, useState} from 'react';
import {Bell, Boxes, ChevronDown, ChevronLeft, ChevronRight, Headset, KeyRound, LayoutDashboard, LogOut, Search, Settings, User, Users, Wallet} from 'lucide-react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useAuth} from '../auth/AuthContext';
import {usePortalAccess} from '../auth/PortalAccessContext';
import {VISOES_DO_PORTAL} from '../auth/portalAccess';
import {environment} from '../../config/environment';

// `visao`: o item só aparece para quem tem a visão do portal (BBF-175). Sem `visao`, aparece para todos.
const menuItems = [
    {id: 'dashboard', label: 'Dashboard', path: '/', icon: LayoutDashboard},
    {id: 'sistemas', label: 'Sistemas', path: '/sistemas', icon: Boxes},
    {id: 'usuarios', label: 'Usuários', path: '/usuarios', icon: Users, visao: VISOES_DO_PORTAL.USUARIOS_E_PERFIS},
    {id: 'licencas', label: 'Licenças', path: '/licencas', icon: KeyRound, visao: VISOES_DO_PORTAL.LICENCAS},
    {id: 'financeiro', label: 'Financeiro', path: '/financeiro', icon: Wallet, visao: VISOES_DO_PORTAL.FINANCEIRA},
    {id: 'chamados', label: 'Chamados', path: '/chamados', icon: Headset, badge: 3, visao: VISOES_DO_PORTAL.SUPORTE},
    {id: 'notificacoes', label: 'Notificações', path: '/notificacoes', icon: Bell, badge: 5},
    {id: 'configuracoes', label: 'Configurações', path: '/configuracoes', icon: Settings},
];

// A página da fatura (/financeiro/:id) deixa o Financeiro marcado no menu.
const isActivePath = (pathname, path) => pathname === path || (path !== '/' && pathname.startsWith(`${path}/`));

export default function PortalShell({children}) {
    const navigate = useNavigate();
    const location = useLocation();
    const {logout, userLogged} = useAuth();
    const {podeVer} = usePortalAccess();
    const [collapsed, setCollapsed] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const initials = useMemo(() => {
        const name = userLogged?.name || userLogged?.username || 'BF';
        return name.split(' ').map((part) => part[0]).join('').slice(0, 2).toUpperCase();
    }, [userLogged]);

    const userName = userLogged?.name || userLogged?.username || 'Cliente';
    const companyName = userLogged?.companyName || userLogged?.empresa?.nome_fantasia || 'BusiFarm';

    return (
        <div className="bf-shell">
            <aside className={`bf-sidebar ${collapsed ? 'bf-sidebar--collapsed' : 'bf-sidebar--expanded'}`}>
                <div className="bf-sidebar__header">
                    {!collapsed ? (
                        <div className="bf-sidebar__brand">
                            <img src="/logo_black.svg" alt="BusiFarm" className="bf-sidebar__logo" />
                        </div>
                    ) : (
                        <div className="bf-sidebar__brand" style={{width: '100%', justifyContent: 'center'}}>
                            <img src="/logo_black.svg" alt="BusiFarm" className="bf-sidebar__icon-logo" style={{objectFit: 'contain'}} />
                        </div>
                    )}
                    <button className="bf-icon-button" onClick={() => setCollapsed((state) => !state)}>
                        {collapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
                    </button>
                </div>

                <nav className="bf-sidebar__nav">
                    {menuItems.filter((item) => podeVer(item.visao)).map((item) => {
                        const Icon = item.icon;
                        const active = isActivePath(location.pathname, item.path);
                        return (
                            <button key={item.id} onClick={() => navigate(item.path)} className={`bf-nav-item ${active ? 'bf-nav-item--active' : ''}`} title={collapsed ? item.label : ''}>
                                <Icon size={20} />
                                {!collapsed ? <span className="bf-nav-item__label">{item.label}</span> : null}
                                {!collapsed && item.badge ? <span className="bf-badge bf-badge--danger">{item.badge}</span> : null}
                                {collapsed && item.badge ? <span className="bf-badge bf-badge--danger" style={{position: 'absolute', top: -4, right: -2, padding: '2px 6px'}}>{item.badge}</span> : null}
                            </button>
                        );
                    })}
                </nav>
            </aside>

            <div className="bf-app">
                <header className="bf-topbar">
                    <div className="bf-topbar__search-wrap">
                        <Search size={18} className="bf-topbar__search-icon" />
                        <input className="bf-topbar__search" placeholder="Pesquisar módulos, usuários, faturas..." />
                    </div>

                    <div className="bf-user-menu" ref={menuRef}>
                        <button className="bf-user-trigger" onClick={() => setMenuOpen((open) => !open)}>
                            <span className="bf-avatar">{initials}</span>
                            <span className="bf-user-meta">
                                <span className="bf-user-name">{userName}</span>
                                <span className="bf-user-company">{companyName}</span>
                            </span>
                            <ChevronDown size={16} color="#64748b" />
                        </button>

                        {menuOpen ? (
                            <div className="bf-user-dropdown">
                                <button className="bf-dropdown-item" onClick={() => { setMenuOpen(false); navigate('/configuracoes'); }}>
                                    <User size={18} />
                                    <span>Perfil</span>
                                </button>
                                <div className="bf-dropdown-divider" />
                                <button
                                    className="bf-dropdown-item bf-dropdown-item--danger"
                                    onClick={() => {
                                        setMenuOpen(false);
                                        logout();
                                        navigate(environment.disableAuth ? '/' : '/login', {replace: true});
                                    }}
                                >
                                    <LogOut size={18} />
                                    <span>Sair</span>
                                </button>
                            </div>
                        ) : null}
                    </div>
                </header>

                <main className="bf-main">{children}</main>
            </div>
        </div>
    );
}
